import './index.css'
import 'react-select/dist/react-select.css'
import cn from 'clsx'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Select, {
  Async as SelectAsync,
  AsyncCreatable,
  Creatable,
} from 'react-select'
import InlineSVG from 'svg-inline-react'
import type { DropdownOption } from 'types/ui'
import { getPopoverDropdownStyle } from '@disco/utils/dom'
import { indexDropdownOptions } from '@disco/utils/search'
import Icon, { IconKey } from 'components/ui/icon'
import Popover from 'components/ui/popover'
import Tag from 'components/ui/tag'
import useEventListener from 'hooks/use-event-listener'

function isOptionUnique({ option, options, labelKey, valueKey }) {
  return (
    options.findIndex(
      existingOption =>
        existingOption[labelKey].toLowerCase() ===
          option[labelKey].toLowerCase() ||
        existingOption[valueKey].toLowerCase() ===
          option[valueKey].toLowerCase(),
    ) === -1
  )
}

const defaultOptionValidation = ({ label }) =>
  !(!label || typeof label !== 'string' || label.trim() === '')

const createValidationFunction = (invalidNewOptions: Array<string>) => {
  if (!invalidNewOptions || invalidNewOptions.length === 0) return undefined
  return function ({ label }) {
    if (!label || typeof label !== 'string' || label.trim() === '') return false

    if (invalidNewOptions && invalidNewOptions.length > 0) {
      return !invalidNewOptions.includes(label)
    }

    return true
  }
}

type Props = {
  async?: boolean
  autoFocus?: boolean
  onBlur?: (...args: Array<any>) => any
  onChange: (...args: Array<any>) => any
  // TODO: fix this any type, check which type is correct in old version of react-select
  value: string | string[] | any
  name?: string
  options?: DropdownOption[]
  optionsIndexed?: (...args: Array<any>) => any
  promptTextCreator?: (...args: Array<any>) => any
  className?: string
  allowCreate?: boolean
  multi?: boolean
  multiSelectLimit?: number
  multiSelectMessage?: string
  clearable?: boolean
  placeholder?: string
  invalidNewOptions?: string[]
  disabled?: boolean
  simpleValue?: boolean
  loadOptions?: (...args: Array<any>) => any
  disabledRemoveTag?: boolean
  scrollContainer?: HTMLElement | null | undefined
  maxMenuHeight?: string
  cache?: boolean
  enableUp?: boolean
  arrowRendererIcon?: IconKey
  iconStrong?: boolean
  scrollToTop?: boolean
  truncateDisplayedOptions?: number
  shortMenu?: boolean
}

/**
 * A highly customizable dropdown component with support for async loading, creation, and multi-select
 * @param async - Whether to use async loading (defaults to false)
 * @param autoFocus - Whether to auto-focus the dropdown on mount
 * @param name - The name attribute for the dropdown
 * @param options - Array of dropdown options
 * @param optionsIndexed - Pre-indexed options for better performance
 * @param value - The current value(s) of the dropdown
 * @param allowCreate - Whether to allow creating new options (defaults to false)
 * @param invalidNewOptions - Array of strings that are invalid as new options
 * @param multi - Whether to enable multi-select mode (defaults to false)
 * @param multiSelectLimit - Maximum number of items that can be selected (defaults to Infinity)
 * @param multiSelectMessage - Message to show when multi-select limit is reached
 * @param clearable - Whether the dropdown value can be cleared (defaults to false)
 * @param placeholder - Placeholder text for the dropdown
 * @param onBlur - Callback fired when the dropdown loses focus
 * @param onChange - Callback fired when the dropdown value changes
 * @param promptTextCreator - Function to create prompt text for new options
 * @param className - Additional CSS classes to apply
 * @param simpleValue - Whether to use simple string values instead of objects
 * @param loadOptions - Function to load options asynchronously
 * @param disabled - Whether the dropdown is disabled (defaults to false)
 * @param disabledRemoveTag - Whether removing tags is disabled in multi-select mode
 * @param scrollContainer - Container element for scroll event handling
 * @param enableUp - Whether to show the dropdown menu upwards
 * @param shortMenu - Whether to use a shorter menu height
 * @param truncateDisplayedOptions - Maximum number of options to display in the menu
 * @param arrowRendererIcon - Icon to use for the dropdown arrow
 * @param cache - Whether to cache async results (defaults to true)
 * @param iconStrong - Whether to use strong styling for the arrow icon
 * @param scrollToTop - Whether to scroll to top when menu opens
 * @returns A customizable dropdown component with various selection modes
 */
const CustomDropdown = ({
  async,
  autoFocus,
  name,
  options,
  optionsIndexed,
  value,
  allowCreate,
  invalidNewOptions,
  multi,
  multiSelectLimit = Infinity,
  multiSelectMessage,
  clearable,
  placeholder,
  onBlur,
  onChange,
  promptTextCreator,
  className,
  simpleValue,
  loadOptions,
  disabled,
  disabledRemoveTag,
  scrollContainer,
  enableUp,
  shortMenu,
  truncateDisplayedOptions,
  arrowRendererIcon,
  cache,
  iconStrong,
  scrollToTop,
}: Props) => {
  const isValidNewOption = invalidNewOptions
    ? createValidationFunction(invalidNewOptions)
    : defaultOptionValidation
  const containerRef = useRef(null)
  const [popoverStyle, setPopoverStyle] = useState(null)
  useEffect(() => {
    if (containerRef.current) {
      const position = scrollContainer ? 'fixed' : 'absolute'
      setPopoverStyle(getPopoverDropdownStyle(containerRef.current, position))
    }
  }, [scrollContainer, containerRef])
  const scrollHandler = useCallback(() => {
    if (!containerRef.current || !scrollContainer) return null
    setPopoverStyle(getPopoverDropdownStyle(containerRef.current, 'fixed'))
  }, [scrollContainer, containerRef])
  useEventListener('scroll', scrollHandler, scrollContainer)
  useEventListener('resize', scrollHandler)

  const menuRenderer = props => {
    let visibleOptions = props.options

    if (truncateDisplayedOptions) {
      visibleOptions = visibleOptions.slice(0, truncateDisplayedOptions)
    }

    const popover =
      props.valueArray.length >= multiSelectLimit ? (
        <div className="popover-item">{multiSelectMessage}</div>
      ) : (
        <Popover
          compact
          scrollToTop={scrollToTop}
          items={visibleOptions}
          selected={props.focusedOption}
          onClick={props.selectValue}
          short={shortMenu}
        />
      )
    return (
      <div style={popoverStyle}>
        <div
          className={cn('custom-dropdown', {
            'popover-item--up': enableUp,
          })}
        >
          {popover}
        </div>
      </div>
    )
  }

  const arrowRenderer = () =>
    arrowRendererIcon ? (
      <Icon
        type={arrowRendererIcon}
        className={cn('inline', {
          arrowRendererIconStrong: iconStrong,
        })}
        width={24}
        height={24}
      />
    ) : (
      <InlineSVG
        src={require('!svg-inline!assets/icons/arrow-down.svg')}
        width="24"
        height="24"
        raw
      />
    )

  const valueComponent = props => {
    const { children, value, onRemove } = props

    const onRemoveClick = () => onRemove(value)

    return (
      <span className="Select-tag">
        <Tag
          tag={children}
          tagColor={value.color}
          onRemoveClick={
            disabled || disabledRemoveTag || value.clearableValue === false
              ? undefined
              : onRemoveClick
          }
          icon={
            value && value.icon ? (
              <Icon
                width={12}
                height={12}
                type={value.icon}
                aria-label={value.label}
              />
            ) : null
          }
        />
      </span>
    )
  }

  const filterOptions = useMemo(() => {
    if (optionsIndexed) return optionsIndexed

    if ((options?.length ?? 0) > 100) {
      // TODO: fallback – remove, and ensure createFilterOptions() is called only from selectors
      return indexDropdownOptions(options)
    }

    return undefined // default react-select behaviour, performant only for SelectAsync or <100 options
  }, [options, optionsIndexed])

  if (async) {
    const asyncSelect = allowCreate ? (
      <AsyncCreatable
        autoFocus={autoFocus}
        arrowRenderer={arrowRenderer}
        menuRenderer={menuRenderer}
        valueComponent={multi ? valueComponent : undefined}
        async={true}
        name={name}
        className={className}
        promptTextCreator={promptTextCreator}
        isOptionUnique={isOptionUnique}
        isValidNewOption={isValidNewOption}
        multi={multi}
        clearable={!!clearable}
        options={options}
        value={value}
        placeholder={placeholder}
        onBlur={onBlur}
        onChange={onChange}
        simpleValue={simpleValue}
        loadOptions={loadOptions}
        disabled={disabled}
      />
    ) : (
      <SelectAsync
        autoFocus={autoFocus}
        cache={!cache ? false : {}}
        arrowRenderer={arrowRenderer}
        menuRenderer={menuRenderer}
        valueComponent={multi ? valueComponent : undefined}
        async={true}
        name={name}
        className={className}
        multi={multi}
        clearable={!!clearable}
        options={options}
        value={value}
        placeholder={placeholder}
        onBlur={onBlur}
        onChange={onChange}
        simpleValue={simpleValue}
        loadOptions={loadOptions}
        disabled={disabled}
        filterOptions={filterOptions}
      />
    )
    return asyncSelect
  }

  const select = allowCreate ? (
    <Creatable
      autoFocus={autoFocus}
      arrowRenderer={arrowRenderer}
      menuRenderer={menuRenderer}
      valueComponent={multi ? valueComponent : undefined}
      name={name}
      className={className}
      promptTextCreator={promptTextCreator}
      isOptionUnique={isOptionUnique}
      isValidNewOption={isValidNewOption}
      multi={multi}
      clearable={!!clearable}
      options={options}
      value={value}
      placeholder={placeholder}
      onBlur={onBlur}
      onChange={onChange}
      simpleValue={simpleValue}
      disabled={disabled}
      filterOptions={filterOptions}
    />
  ) : (
    <Select
      autoFocus={autoFocus}
      arrowRenderer={arrowRenderer}
      menuRenderer={menuRenderer}
      valueComponent={multi ? valueComponent : undefined}
      name={name}
      className={className}
      multi={multi}
      clearable={!!clearable}
      options={options}
      value={value}
      placeholder={placeholder}
      onBlur={onBlur}
      onChange={onChange}
      simpleValue={simpleValue}
      disabled={disabled}
      filterOptions={filterOptions}
    />
  )
  return (
    <div
      className={cn('custom-dropdown', {
        'select-up': enableUp,
      })}
      ref={containerRef}
    >
      {select}
    </div>
  )
}

export default CustomDropdown
